import React from "react";
import "./about.css";
import Header from "../components/layout/Header";
import Footer from "./Footer";
import { Link } from "react-router-dom";

import heroImg from "../assets/suvidha-about-hero-no-text.png";

import {
  FaUserFriends,
  FaUserNurse,
  FaHome,
  FaPills,
  FaHospitalAlt,
  FaHeart,
  FaWheelchair,
  FaUsers,
  FaUserCircle,
  FaHandHoldingHeart,
  FaSyncAlt,
} from "react-icons/fa";

const SERVICES = [
  { icon: <FaUserFriends />, label: "Personalised Elder Care" },
  { icon: <FaUserNurse />,   label: "Attendant & Caregiver Support" },
  { icon: <FaHome />,        label: "Daily Living Assistance" },
  { icon: <FaPills />,       label: "Medication & Health Coordination" },
  { icon: <FaHospitalAlt />, label: "Hospital & Post-Hospital Support" },
  { icon: <FaHeart />,       label: "Companionship & Emotional Support" },
  { icon: <FaWheelchair />,  label: "Mobility & Rehabilitation" },
  { icon: <FaHome />,        label: "Home-Based Elder Care" },
  { icon: <FaUsers />,       label: "Family & Caregiver Guidance" },
];

const APPROACH_POINTS = [
  { icon: <FaHandHoldingHeart />, label: "Personalised Around Their Routine" },
  { icon: <FaUsers />,            label: "Coordinated With Families and Professionals" },
  { icon: <FaSyncAlt />,          label: "Adapted as Their Needs Change" },
];

// Placeholder leadership data — replace with real names, designations,
// bios and photos when available.
const LEADERS = [
  {
    name: "[Director Name]",
    designation: "[Designation]",
    bio:
      "Director introduction — approximately 100–130 words covering professional background, role at Suvidha, connection to elder care and vision for the organisation. This space can be used to share their experience, key areas of expertise, what drives their commitment to senior well-being and how they contribute to shaping Suvidha's work towards a more caring, supportive and dignified ageing experience for every senior.",
    photo: null,
  },
  {
    name: "[Director Name]",
    designation: "[Designation]",
    bio:
      "Director introduction — approximately 100–130 words covering professional background, role at Suvidha, connection to elder care and vision for the organisation. This space can be used to share their experience, key areas of expertise, what drives their commitment to senior well-being and how they contribute to shaping Suvidha's work towards a more caring, supportive and dignified ageing experience for every senior.",
    photo: null,
  },
  {
    name: "[Director Name]",
    designation: "[Designation]",
    bio:
      "Director introduction — approximately 100–130 words covering professional background, role at Suvidha, connection to elder care and vision for the organisation. This space can be used to share their experience, key areas of expertise, what drives their commitment to senior well-being and how they contribute to shaping Suvidha's work towards a more caring, supportive and dignified ageing experience for every senior.",
    photo: null,
  },
];

function DirectorPhoto({ photo, name }) {
  if (photo) return <img src={photo} alt={name} className="au-leaderPhotoImg" />;
  return (
    <div className="au-leaderPhotoPlaceholder">
      <FaUserCircle className="au-leaderPhotoIcon" />
      <span>DIRECTOR PHOTO</span>
    </div>
  );
}

function Aboutus() {
  return (
    <>
      <Header />

      {/* ============ HERO ============ */}
      <section className="au-hero">
        <div className="au-heroBg" style={{ backgroundImage: `url(${heroImg})` }} />
        <div className="au-heroInner">
          <span className="au-eyebrow">About Suvidha</span>
          <h1 className="au-heroTitle">
            Care That Protects Dignity<br />and Supports Independence.
          </h1>
          <span className="au-underline" />
          <p className="au-heroText">
            Suvidha Elder Care provides compassionate, reliable and personalised
            support for senior citizens — helping them live safely, confidently
            and comfortably while keeping their families reassured.
          </p>
        </div>
      </section>

      {/* ============ AGEING DESERVES MORE ============ */}
      <section className="au-ageing">
        <div className="au-ageingLeft">
          <h2>Ageing Deserves<br />More Than Assistance.</h2>
          <span className="au-underline" />
          <p>
            Ageing is a meaningful stage of life that deserves respect,
            companionship, comfort and attentive support. At Suvidha, we help
            seniors preserve their routines, choices and independence while
            ensuring dependable care is always within reach.
          </p>
        </div>
        <span className="au-ageingDivider" />
        <div className="au-ageingRight">
          <span className="au-quoteMark au-quoteMarkTop">&ldquo;</span>
          <p>Care should help seniors continue living life on their terms.</p>
          <span className="au-underline au-underlineOrange" />
          <span className="au-quoteMark au-quoteMarkBottom">&rdquo;</span>
        </div>
      </section>

      {/* ============ OUR SERVICES ============ */}
      <section className="au-services">
        <div className="au-sectionHead">
          <span className="au-eyebrow">Our Services</span>
          <h2>Support Designed Around Every Senior.</h2>
        </div>

        <div className="au-serviceGrid">
          {SERVICES.map((s, i) => (
            <div
              key={i}
              className={`au-serviceCard ${i % 2 === 0 ? "au-cardBlue" : "au-cardCream"}`}
            >
              <span className="au-serviceIcon">{s.icon}</span>
              <p>{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ OUR APPROACH ============ */}
      <section className="au-approach">
        <span className="au-eyebrow au-eyebrowOnDark">Our Approach</span>
        <h2>Care That Sees the Person, Not Just the Need.</h2>
        <p className="au-approachIntro">
          Every senior has unique needs, preferences and life experiences. Our
          approach supports physical well-being while giving equal importance
          to emotional comfort, companionship, safety and quality of life.
        </p>

        <div className="au-approachRow">
          {APPROACH_POINTS.map((p, i) => (
            <React.Fragment key={i}>
              <div className="au-approachItem">
                <span className="au-approachIcon">{p.icon}</span>
                <p>{p.label}</p>
              </div>
              {i < APPROACH_POINTS.length - 1 && <span className="au-approachDivider" />}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ============ OUR LEADERSHIP ============ */}
      <section className="au-leadership">
        <div className="au-sectionHead au-sectionHeadLeft">
          <span className="au-eyebrow">Our Leadership</span>
          <h2>The People Guiding Suvidha.</h2>
          <p className="au-leadershipIntro">
            Leadership shaped by responsibility, empathy and a commitment to
            dependable elder care.
          </p>
        </div>

        {LEADERS.map((leader, i) => (
          <div
            key={i}
            className={`au-leaderRow ${i % 2 === 1 ? "au-leaderRowReverse" : ""}`}
          >
            <div className="au-leaderPhoto">
              <DirectorPhoto photo={leader.photo} name={leader.name} />
            </div>
            <div className="au-leaderInfo">
              <h3>{leader.name}</h3>
              <span className="au-leaderDesignation">{leader.designation}</span>
              <p>{leader.bio}</p>
            </div>
          </div>
        ))}
      </section>

      {/* ============ BOTTOM CTA ============ */}
      <section className="au-cta">
        <svg className="au-ctaWave" viewBox="0 0 1440 80" preserveAspectRatio="none">
          <path d="M0,80 C360,0 1080,0 1440,80 L1440,80 L0,80 Z" fill="var(--dark-primary, #001429)" />
        </svg>

        <div className="au-ctaInner">
          <div>
            <h2>Dignity in Every Day.<br />Reassurance for Every Family.</h2>
            <p>Suvidha Elder Care is here to make ageing safer, more comfortable and better supported.</p>
          </div>
          <Link to="/Contact" className="au-ctaBtn">Connect With Us</Link>
        </div>

        <svg className="au-ctaBottomLine" viewBox="0 0 1440 40" preserveAspectRatio="none">
          <path
            d="M0,20 C240,60 480,-20 720,20 C960,60 1200,-20 1440,20"
            fill="none"
            stroke="var(--base-secondary, #F17A00)"
            strokeWidth="2"
          />
        </svg>
      </section>

      <Footer />
    </>
  );
}

export default Aboutus;